import React, { useState, useEffect, FC } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  loadMorePosts,
  refreshPosts,
  toggleUpvote,
  toggleDownvote,
  toggleReaction,
} from '../reducers/postReducer';
import { notify } from '../reducers/notificationReducer';
import PostCard from './PostCard';
import PostCardSkeleton from './PostCardSkeleton';
import FeedViewBar from './FeedViewBar';
import InfiniteScrollSentinel from './InfiniteScrollSentinel';
import EmptyState from './EmptyState';
import EndOfFeed from './EndOfFeed';
import LoadingSpinner from './LoadingSpinner';
import getErrorMsg from '../utils/getErrorMsg';
import { useScrollRestoration } from '../utils/useScrollRestoration';
import posthog from '../utils/posthog';
import type { PostItem } from '../types/common';

import { Box } from '@mui/material';

const PostList: FC = () => {
  const posts = useAppSelector((state) => state.posts);
  const [viewMode, setViewMode] = useState('expanded');
  // The feed data survives navigation, so derive the pagination cursor from it:
  // starting back at 1 after a restore would refetch page 2 into a 4-page list
  // and duplicate posts.
  const [page, setPage] = useState(() => Math.max(1, Math.ceil((posts?.results?.length ?? 0) / 10)));
  const [loadingMore, setLoadingMore] = useState(false);
  const dispatch = useAppDispatch();
  const restoreAnchor = useScrollRestoration();

  const handleViewChange = (value: string) => {
    setViewMode(value);
  };

  // Periodically refresh the feed (web) so new posts surface without a hard
  // reload. Only refreshes the first page to avoid disrupting users who have
  // paginated further, and pauses while the tab is hidden.
  useEffect(() => {
    const REFRESH_INTERVAL_MS = 60000;
    const interval = setInterval(() => {
      if (page === 1 && document.visibilityState === 'visible') {
        dispatch(refreshPosts()).catch(() => {});
      }
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [dispatch, page]);

  const handleLoadPosts = async () => {
    try {
      setLoadingMore(true);
      await dispatch(loadMorePosts(page + 1));
      posthog.capture('feed_refreshed', { page: page + 1 });
      setPage((prevState) => prevState + 1);
      setLoadingMore(false);
    } catch (err) {
      setLoadingMore(false);
      dispatch(notify(getErrorMsg(err), 'error'));
    }
  };

  return (
    <Box ref={restoreAnchor} sx={{ width: '100%', mb: 'var(--space-stack-normal)' }}>
      <FeedViewBar viewMode={viewMode} handleViewChange={handleViewChange} />
      {posts && posts.results ? (
        posts.results.map((post: PostItem) => (
          <React.Fragment key={post.id}>
            <PostCard
              post={post as any}
              toggleUpvote={toggleUpvote}
              toggleDownvote={toggleDownvote}
              toggleReaction={toggleReaction}
              variant={viewMode === 'expanded' ? 'expanded' : 'compact'}
            />
          </React.Fragment>
        ))
      ) : (
        <LoadingSpinner text={'Fetching posts. Wait a sec.'} />
      )}
      {posts && posts.results && posts.results.length === 0 && (
        <EmptyState
          title="Your herd is quiet"
          subtitle="Join a few herds or pick topics in Preferences to fill this feed."
          sx={{ mt: 'var(--space-empty-state-offset)' }}
        />
      )}
      {posts && posts.results && (
        <InfiniteScrollSentinel
          onLoadMore={handleLoadPosts}
          loading={loadingMore}
          hasMore={'next' in posts}
          totalCount={posts.totalCount as number | undefined}
          loadedCount={posts.results.length}
          renderSkeleton={(i) => (
            <PostCardSkeleton key={i} variant={viewMode === 'expanded' ? 'expanded' : 'compact'} />
          )}
        />
      )}
      {posts && posts.results && posts.results.length > 0 && !('next' in posts) && (
        <EndOfFeed />
      )}
    </Box>
  );
};

export default PostList;
